import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminStats } from "../lib/api";
import { Card } from "./AdminLayout";
import { useAuth } from "../context/AuthContext";
import ReporterHome from "./ReporterHome";
import { Newspaper, Images, Store, Megaphone, MessageSquare, Clock, Plus, CalendarDays, ClipboardCheck } from "lucide-react";

const CARDS = [
  { key: "articles", label: "Published Articles", icon: Newspaper, to: "/admin/articles", color: "text-blue-600" },
  { key: "galleries", label: "Photo Galleries", icon: Images, to: "/admin/galleries", color: "text-pink-600" },
  { key: "events", label: "Live Events", icon: CalendarDays, to: "/admin/events", color: "text-teal-600" },
  { key: "pending_events", label: "Events to Approve", icon: Clock, to: "/admin/events", color: "text-amber-600" },
  { key: "pending_posts", label: "Posts to Review", icon: ClipboardCheck, to: "/admin/review", color: "text-orange-600" },
  { key: "businesses", label: "Directory Listings", icon: Store, to: "/admin/directory", color: "text-emerald-600" },
  { key: "ads", label: "Active Ad Units", icon: Megaphone, to: "/admin/ads", color: "text-rose-600" },
  { key: "social_posts", label: "Social Posts", icon: MessageSquare, to: "/admin/social", color: "text-sky-600" },
];

const QUICK = [
  { label: "Write a new article", to: "/admin/articles", icon: Newspaper },
  { label: "Review community posts", to: "/admin/review", icon: ClipboardCheck },
  { label: "Add a community event", to: "/admin/events", icon: CalendarDays },
  { label: "Upload a photo gallery", to: "/admin/galleries", icon: Images },
];

export default function Dashboard() {
  const { user } = useAuth();
  return user?.role === "admin" ? <EditorDashboard /> : <ReporterHome user={user} />;
}

function EditorDashboard() {
  const [stats, setStats] = useState({});
  useEffect(() => { adminStats().then(setStats).catch(() => {}); }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6 rounded-xl bg-[var(--navy)] p-6 text-white">
        <h2 className="font-display text-2xl font-bold">Welcome to the News-Leader newsroom</h2>
        <p className="mt-1 text-sm text-white/75">
          Reporters, editors and ad operations staff manage everything that appears on the public site from here — articles, photos, the business directory, banner & native ads, and the social feed.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {CARDS.map((c) => (
          <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`} className="block min-w-0">
            <Card className="card-hover min-w-0 p-5">
              <c.icon className={c.color} size={22} />
              <p className="mt-3 text-3xl font-extrabold text-[var(--navy)]">{stats[c.key] ?? "—"}</p>
              <p className="truncate text-xs text-slate-500">{c.label}</p>
            </Card>
          </Link>
        ))}
      </div>

      <h3 className="mb-3 mt-8 text-lg font-bold text-[var(--navy)]">Quick actions</h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {QUICK.map((q) => (
          <Link key={q.label} to={q.to}>
            <Card className="card-hover flex items-center gap-3 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--gold)]/15 text-[var(--gold)]"><q.icon size={18} /></span>
              <span className="text-sm font-semibold text-slate-700">{q.label}</span>
              <Plus size={16} className="ml-auto text-slate-400" />
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
